import { defineComponent as _defineComponent } from 'vue'
import {
  nextTick,
  useNuxt2Meta,
  useNuxtApp,
  usePinia,
  useRoute,
  useRuntimeConfig,
  useSearchStore,
} from '#imports'
import { Ref, computed, onMounted, ref, watch } from 'vue'
import {
  MetaInfo,
  MetaPropertyCharset,
  MetaPropertyEquiv,
  MetaPropertyMicrodata,
  MetaPropertyName,
  MetaPropertyProperty,
  ScriptPropertyJson,
  ScriptPropertySrc,
  ScriptPropertySrcCallback,
  ScriptPropertyText,
} from 'vue-meta'
import { PaginationData } from '~/utility/pagination'
import { isPointSearch } from '../domain/isPointSearch'
import { HierarchyTrail, SimpleGeoPoint } from '../schemas'


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchMain',
  props: {
    query: null,
    placename: null,
    selectedCategoryIds: null
  },
  setup(__props: any) {

const props = __props as {
  query?: string
  placename?: string
  selectedCategoryIds?: Array<string>
};

const { $puScrollTo, $nextTick, $route } = useNuxtApp()



const isDev = process.env.NODE_ENV !== 'production'
if (isDev) console.log('SearchMain.setup', process.client, $route.fullPath)

const searchStore = useSearchStore(usePinia())

onMounted(() => {
  if (isDev)
    console.log('SearchMain.onMounted', process.client, $route.fullPath)
  const elements = document.querySelectorAll('[data-lang]')
  const lang = route.params.lang || 'en-gb'
  elements.forEach((element) => {
    if (element.getAttribute('data-lang') !== lang) {
      console.error(
        `Unexpected lang attribute: ${element.getAttribute(
          'data-lang',
        )}. Element: ${element.tagName}, Class: ${element.className}, Text: ${
          element.textContent
        }`,
      )
    }
  })
})

const firstResultImgSrcSet = computed(() => {
  const img = searchStore.context.results?.campsites[0]?.primaryPhoto
  if (!img) {
    return
  }
  return `
    /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${img.url.masterImage} 360w,
    /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=720,h=540${img.url.masterImage} 720w,
    /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${img.url.masterImage} 1080w
  `
})

const firstResultImg = computed(() => {
  const img = searchStore.context.results?.campsites[0]?.primaryPhoto
  if (!img) {
    return
  }
  // match PrimaryPhoto.vue
  return `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=15,w=428,h=321${img.url.masterImage}`
})

const visibleCampsite: Ref<{ slug: string; point: string } | null> = ref(null)
const headerIsVisible = ref(true)
const scrolledDown = ref(false)
// const searchBarDesktopFilters: Ref<SearchFilters | null> = ref(null)

const faqList = computed(() => {
  return searchStore.context.results?.faq
})

const cityPoint = computed((): SimpleGeoPoint | undefined => {
  return searchStore.context.results?.point || undefined
})

const heading = computed((): string | undefined => {
  return searchStore.context.results?.heading
})

const title = computed((): string | undefined => {
  return searchStore.context.results?.title
})

const summaryProps = computed(() => {
  const results = searchStore.context.results
  return results
    ? {
      bookableCount: results.bookableCount || 0,
      campsitesCount: results.count || 0,
      hierarchyName: results.hierarchyName || undefined,
      distance: results.within || undefined,
      categoryName: results.category?.name,
      isUserLocationSearch:
          results.within && results.point && !results.hierarchyName,
      isAvailabilitySearch: searchStore.isDated,
    }
    : undefined
})

const hierarchyTrail = computed((): HierarchyTrail[] | undefined => {
  return searchStore.context.results?.hierarchyTrail
})

const currenthierarchyItem = computed((): HierarchyTrail | undefined => {
  const results = searchStore.context.results
  return hierarchyTrail.value && results?.hierarchyName
    ? {
      name: results.hierarchyName,
      path: hierarchyTrail.value[hierarchyTrail.value.length - 1].path,
    }
    : undefined
})

const sortOption = computed(() => {
  return searchStore.context.results?.sortOption
})

function headerVisibilityChanged(isVisible: boolean) {
  headerIsVisible.value = isVisible
}

const isIntentionalPointSearch = computed(() => {
  if (!searchStore.searchFilters) {
    return false
  }
  const isPointSearchTypeAndFilters = isPointSearch(searchStore.searchFilters)
  const isBoundedSearch = !!searchStore.searchFilters?.bounds
  return isPointSearchTypeAndFilters && !isBoundedSearch
})

function scrolledResultsDown(state: boolean) {
  scrolledDown.value = state
}

const pagePart = computed(() => {
  return searchStore.context.results?.pagePart
})

const campsites = computed(() => {
  return searchStore.context.results?.campsites || []
})

const paginationData = computed((): PaginationData | undefined => {
  return searchStore.context.results?.pagination || undefined
})

const states = computed(() => {
  return searchStore.states
})

watch(states, (newStates) => {
  if (newStates.includes('results')) {
    nextTick(() => {
      $puScrollTo('[data-new-results-scroll-target]', { duration: 300 })
    })
  }
})

const count = computed(() => {
  const count =
    searchStore.context.results?.fullBookableCount === undefined
      ? searchStore.context.results?.count
      : searchStore.context.results?.fullBookableCount
  return count || 0
})

const shouldShowLowResultsPanel = computed(() => count.value < 6)

const isCountyOrCity = computed(
  () => hierarchyTrail.value && hierarchyTrail.value.length >= 3,
)

const shouldShowNotifyMe = computed(
  () =>
    shouldShowLowResultsPanel.value &&
    searchStore.searchFilters?.dates &&
    (!searchStore.searchFilters?.hierarchyPath ||
      (!!searchStore.searchFilters?.hierarchyPath && isCountyOrCity.value)),
)

const firstResult = computed(() => {
  return (
    searchStore.context.results?.campsites &&
    searchStore.context.results?.campsites.length &&
    searchStore.context.results?.campsites[0]
  )
})

/*
  SEO Meta Data
*/

const runtimeConfig = useRuntimeConfig()
const nuxtApp = useNuxtApp()

const imageObjects = computed(() => {
  const scriptData: (
    | ScriptPropertyText
    | ScriptPropertySrc
    | ScriptPropertySrcCallback
    | ScriptPropertyJson
  )[] = []
  if (facetMetaData.value?.og_image) {
    scriptData.push({
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        description: facetMetaData.value.name,
        contentUrl: `https://${runtimeConfig.public.envUrl}${facetMetaData.value.og_image}`,
      },
    })
  }
  if (searchStore?.context?.results?.campsites?.length) {
    const numberOfResults = searchStore.context.results.campsites.length
    for (let i = 0; i < numberOfResults; i++) {
      if (searchStore.context.results?.campsites[i].bookable) {
        scriptData.push(schemaImageObject(i))
      }
    }
  }
  return scriptData
})

const showMap = ref(true)

function showMapEvent(event: boolean): void {
  showMap.value = event
}

// function searchBarDesktopFiltersUpdate(filters: SearchWizardFilters) {
//   searchBarDesktopFilters.value = filters
// }

// const searchBarFilters = computed(() => {
//   return searchBarDesktopFilters.value
//     ? { ...searchStore.searchFilters, ...searchBarDesktopFilters.value }
//     : searchStore.searchFilters
// })

function mapMarkerOpened(slug: string): void {
  document.querySelectorAll('.search-card.map-marker-opened').forEach((li) => {
    li.classList.remove('map-marker-opened')
  })
  const csDataAttribute = `li[data-slug="${slug}"]`
  const activeCard = document.querySelector(csDataAttribute) as HTMLElement
  if (activeCard) {
    activeCard.classList.add('map-marker-opened')
    $nextTick(() => {
      $puScrollTo(csDataAttribute, { duration: 300, offset: -200 })
    })
  }
}

function schemaImageObject(index: number) {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    name: searchStore.context.results?.campsites[index].name,
    contentLocation: `${searchStore.context.results?.campsites[index].name}, ${searchStore.context.results?.campsites[index].hierarchyStringShort}`,
    contentUrl: `https://${runtimeConfig.public.envUrl}/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${searchStore.context.results?.campsites[index].primaryPhoto?.url.masterImage}`,
  }
  if (nuxtApp.$i18n.locale.split('-')[0] === 'en') {
    json['description'] =
      searchStore.context.results?.campsites[index].primaryPhoto?.caption
  }
  return {
    type: 'application/ld+json',
    body: true,
    json,
  }
}

const ogTitle = computed(() => {
  return {
    hid: 'og:title',
    property: 'og:title',
    content: title.value || '',
  }
})

const ogDescription = computed(() => {
  return {
    hid: 'description',
    name: 'description',
    property: 'og:description',
    content: searchStore.context.results?.description || '',
    'data-brainlabs-count': count.value,
    'data-brainlabs-price': searchStore.context.results?.leadPrice
      ? `${searchStore.context.results?.leadPrice[0].currency} ${searchStore.context.results?.leadPrice[0].amount}`
      : '',
  }
})

const route = useRoute()

const ogUrl = computed(() => {
  return {
    hid: 'og:url',
    property: 'og:url',
    content: `https://${runtimeConfig.public.envUrl}${route.path}`,
  }
})

const ogImage = computed(() => {
  if (firstResult.value) {
    return {
      hid: 'og:image',
      property: 'og:image',
      content: `https://${runtimeConfig.public.envUrl}${firstResult.value.primaryPhoto?.url.masterImage}`,
    }
  }
  return {
    hid: '',
    property: '',
    content: '',
  }
})

const ogImageCaption = computed(() => {
  if (firstResult.value && nuxtApp.$i18n.locale.split('-')[0] === 'en') {
    return {
      hid: 'og:image:alt',
      property: 'og:image:alt',
      content: firstResult.value.primaryPhoto?.caption || '',
    }
  }
  return {
    hid: '',
    property: '',
    content: '',
  }
})

const ogImageWidth = {
  hid: 'og:image:width',
  property: 'og:image:width',
  content: 3200,
}

const ogImageHeight = {
  hid: 'og:image:height',
  property: 'og:image:height',
  content: 2400,
}

const facetOgImageWidth = {
  hid: 'og:image:width',
  property: 'og:image:width',
  content: 1800,
}

const facetOgImageHeight = {
  hid: 'og:image:height',
  property: 'og:image:height',
  content: 1350,
}

const isCityLevel = computed(
  () => hierarchyTrail.value && hierarchyTrail.value.length >= 4,
)

const facetMetaData = computed(() => {
  if (
    searchStore.searchFilters?.facets &&
    searchStore.searchFilters.facets?.length === 1 &&
    searchStore.context.results?.facetModels
  ) {
    const facet = searchStore.searchFilters.facets[0]
    return searchStore.context.results?.facetModels[facet] || ''
  }
  return null
})

const facetOgImage = computed(() => {
  return {
    hid: 'og:image',
    property: 'og:image',
    content: `https://${runtimeConfig.public.envUrl}${facetMetaData.value?.og_image}`,
  }
})

const facetOgImageCaption = computed(() => {
  return {
    hid: 'og:image:alt',
    property: 'og:image:alt',
    content: facetMetaData.value?.name || '',
  }
})

const headData: MetaInfo = {
  title: title.value || 'Pitchup.com search',
  meta: [ogTitle.value, ogDescription.value, ogUrl.value],
  script: imageObjects.value,
}

const facetOgImageMeta: (
  | MetaPropertyCharset
  | MetaPropertyEquiv
  | MetaPropertyName
  | MetaPropertyMicrodata
  | MetaPropertyProperty
)[] = [
  facetOgImage.value,
  facetOgImageCaption.value,
  facetOgImageWidth,
  facetOgImageHeight,
]

const ogImageMeta: (
  | MetaPropertyCharset
  | MetaPropertyEquiv
  | MetaPropertyName
  | MetaPropertyMicrodata
  | MetaPropertyProperty
)[] = [ogImage.value, ogImageCaption.value, ogImageWidth, ogImageHeight]

if (facetMetaData.value?.og_image) {
  headData.meta = [...(<[]>headData.meta), ...facetOgImageMeta]
} else {
  headData.meta = [...(<[]>headData.meta), ...ogImageMeta]
}

headData['link'] = headData['link'] || []

// Preloading the srcset for first image in first result gallery for
// when we set image srcset on user interaction.
if (process.server && firstResultImg.value) {
  headData['link'].push({
    rel: 'preload',
    as: 'image',
    href: firstResultImg.value,
  })
}

if (
  paginationData.value &&
  paginationData.value?.currentPage &&
  paginationData.value?.currentPage > 1
) {
  headData['link'].push({
    hid: 'i18n-can',
    rel: 'canonical',
    href: `https://${runtimeConfig.public.envUrl}${route.path}?page=${paginationData.value?.currentPage}`,
  })
}

useNuxt2Meta(headData)

return { __sfc: true,$puScrollTo, $nextTick, $route, props, isDev, searchStore, firstResultImgSrcSet, firstResultImg, visibleCampsite, headerIsVisible, scrolledDown, faqList, cityPoint, heading, title, summaryProps, hierarchyTrail, currenthierarchyItem, sortOption, headerVisibilityChanged, isIntentionalPointSearch, scrolledResultsDown, pagePart, campsites, paginationData, states, count, shouldShowLowResultsPanel, isCountyOrCity, shouldShowNotifyMe, firstResult, runtimeConfig, nuxtApp, imageObjects, showMap, showMapEvent, mapMarkerOpened, schemaImageObject, ogTitle, ogDescription, route, ogUrl, ogImage, ogImageCaption, ogImageWidth, ogImageHeight, facetOgImageWidth, facetOgImageHeight, isCityLevel, facetMetaData, facetOgImage, facetOgImageCaption, headData, facetOgImageMeta, ogImageMeta }
}

})